import { FunctionComponent, ReactElement, useCallback, useState } from "react";

import { Alert, Chip, Typography, styled } from "@mui/material";

import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridValueGetterParams,
} from "@mui/x-data-grid";
import { format } from "date-fns";
import { useParams } from "react-router-dom";

import {
    CircularLoader,
    DataGridFooter,
    EmptyRecords,
    WorkspaceToolbar,
} from "../../components/common";
import {
    useGetVariantInventory,
    useGetVariantStockHistory,
} from "../../sdk/hooks";
import {
    StockOverrideReason,
    StockStatus,
    StockStatusSource,
    stockOverrideReasonMap,
} from "../../sdk/utils/api";

const PAGE_SIZE = 25;

const Root = styled("section")`
    padding: ${({ theme }) => theme.spacing(3)};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
`;

const Caption = styled(Typography)`
    font-size: 12px;
    color: #626262;
`;

const EmptyRecordsContainer = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1)};
    align-items: center;
`;

const Title = styled(Typography)`
    font-size: 24px;
    font-weight: 500;
`;

const StyledDataGrid = styled(DataGrid)`
    .MuiDataGrid-cell:focus {
        outline: none;
    }
`;

const columns: GridColDef[] = [
    {
        field: "createdAt",
        headerName: "When",
        flex: 0.2,
        minWidth: 170,
        valueGetter: (params: GridValueGetterParams) =>
            format(new Date(params.row.createdAt), "dd MMM yyyy, HH:mm:ss"),
    },
    {
        field: "status",
        headerName: "Changed to",
        flex: 0.15,
        minWidth: 130,
        renderCell: (params: GridRenderCellParams) => (
            <Chip
                size="small"
                label={
                    params.row.status === StockStatus.OOS
                        ? "Out of stock"
                        : "In stock"
                }
                color={
                    params.row.status === StockStatus.OOS ? "error" : "success"
                }
            />
        ),
    },
    {
        field: "source",
        headerName: "Source",
        flex: 0.1,
        minWidth: 100,
        valueGetter: (params: GridValueGetterParams) =>
            params.row.source === StockStatusSource.MANUAL ? "Manual" : "Sync",
    },
    {
        field: "createdBy",
        headerName: "By",
        flex: 0.2,
        minWidth: 160,
        valueGetter: (params: GridValueGetterParams) => {
            const admin = params.row.createdBy;

            if (!admin) {
                return "Automated sync";
            }

            return (
                [admin.firstName, admin.lastName].filter(Boolean).join(" ") ||
                admin.emailAddress
            );
        },
    },
    {
        field: "reason",
        headerName: "Reason",
        flex: 0.25,
        minWidth: 180,
        valueGetter: (params: GridValueGetterParams) => {
            const label = params.row.reason
                ? stockOverrideReasonMap[
                      params.row.reason as StockOverrideReason
                  ]
                : "Automated evaluation";

            return params.row.note ? `${label}: ${params.row.note}` : label;
        },
    },
    {
        field: "triggerQuantity",
        headerName: "Quantity",
        flex: 0.1,
        minWidth: 100,
        valueGetter: (params: GridValueGetterParams) =>
            params.row.triggerQuantity ?? "N/A",
    },
    {
        field: "effectiveCap",
        headerName: "Cap",
        flex: 0.1,
        minWidth: 90,
        valueGetter: (params: GridValueGetterParams) =>
            params.row.effectiveCap ?? "N/A",
    },
];

export const VariantStockHistoryScreen: FunctionComponent = (): ReactElement => {
    const params = useParams();
    const variantId = params.variantId ?? "";

    const [page, setPage] = useState<{ cursor?: string; direction: string }>({
        cursor: undefined,
        direction: "after",
    });

    const inventoryQuery = useGetVariantInventory(variantId);
    const historyQuery = useGetVariantStockHistory({
        variantId,
        limit: PAGE_SIZE,
        cursor: page.cursor,
        direction: page.direction,
    });

    const handleNavigateBack = useCallback(() => {
        setPage({
            cursor: historyQuery.data?.cursors?.previous ?? "",
            direction: "before",
        });
    }, [historyQuery.data?.cursors?.previous]);

    const handleNavigateForward = useCallback(() => {
        setPage({
            cursor: historyQuery.data?.cursors?.next ?? "",
            direction: "after",
        });
    }, [historyQuery.data?.cursors?.next]);

    const inventory = inventoryQuery.data;
    const records = historyQuery.data?.records ?? [];

    if (historyQuery.isLoading || inventoryQuery.isLoading) {
        return <CircularLoader />;
    }

    return (
        <>
            <WorkspaceToolbar
                title="Stock History"
                enableSearch={false}
                breadcrumbs={[
                    { title: "Products", link: "/products" },
                    {
                        title: inventory?.product?.title ?? "Product",
                        link: `/products/${inventory?.variant.productId ?? ""}`,
                    },
                    {
                        title: inventory?.variant.title ?? "Variant",
                        link: `/products/${inventory?.variant.productId ?? ""}/variants/${variantId}/edit`,
                    },
                    { title: "Stock History", link: "" },
                ]}
            />

            <Root>
                {inventory && (
                    <Caption>
                        SKU {inventory.variant.sku}. Every stock status event for
                        this variant, newest first: engine flips, and admin
                        overrides whether or not they changed the status.
                    </Caption>
                )}

                {historyQuery.isError && (
                    <Alert severity="error">
                        Could not load stock history for this variant.
                    </Alert>
                )}

                {records.length === 0 && !historyQuery.isError ? (
                    <EmptyRecords>
                        <EmptyRecordsContainer>
                            <Title>No stock changes yet</Title>
                            <Typography>
                                This variant has not flipped between in stock
                                and out of stock.
                            </Typography>
                        </EmptyRecordsContainer>
                    </EmptyRecords>
                ) : (
                    <StyledDataGrid
                        rows={records}
                        columns={columns}
                        autoHeight={true}
                        disableRowSelectionOnClick={true}
                        hideFooterPagination={true}
                        slots={{
                            footer: () => (
                                <DataGridFooter
                                    onNavigateBack={handleNavigateBack}
                                    onNavigateForward={handleNavigateForward}
                                    hasPreviousRecords={Boolean(
                                        historyQuery.data?.cursors?.previous,
                                    )}
                                    hasNextRecords={Boolean(
                                        historyQuery.data?.cursors?.next,
                                    )}
                                />
                            ),
                        }}
                    />
                )}
            </Root>
        </>
    );
};
